import { ShieldCheck } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageLayout } from '@/components/Layout/PageLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/Common/Card';
import { Button } from '@/components/Common/button';
import { Input } from '@/components/Common/input';
import { Label } from '@/components/Common/label';
import superadminService from '@/services/superadminService';
import notify from '@/lib/notify';
import type { PromoteResponse } from '@/services/superadminService';

const SuperadminSetupPage: React.FC = () => {
  const { t } = useTranslation();
  const [setupToken, setSetupToken] = useState('');
  const [userId, setUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<PromoteResponse | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = setupToken.trim();
    const id = userId.trim();
    if (!token || !id) {
      notify.error(t('admin.superadminSetup.errors.missingFields', 'Please fill setup token and user ID'));
      return;
    }
    setLoading(true);
    setSuccess(null);
    try {
      const data = await superadminService.promoteToSuperadmin(id, token);
      setSuccess(data);
      notify.success(t('admin.superadminSetup.success', 'User promoted to super-admin.'));
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        t('admin.superadminSetup.errors.failed', 'Request failed');
      notify.error(String(message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout
      icon={<ShieldCheck className="h-6 w-6" />}
      title={t('admin.superadminSetup.title')}
      subtitle={t('admin.superadminSetup.subtitle')}
    >
      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>{t('admin.superadminSetup.title')}</CardTitle>
          <CardDescription>{t('admin.superadminSetup.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="token">{t('admin.superadminSetup.setupToken')}</Label>
              <Input
                id="token"
                type="password"
                value={setupToken}
                onChange={(e) => setSetupToken(e.target.value)}
                placeholder={t('admin.superadminSetup.setupTokenPlaceholder')}
                autoComplete="off"
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="userId">{t('admin.superadminSetup.userId')}</Label>
              <Input
                id="userId"
                type="text"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="753bbdde-25b8-4ed5-bc68-44e0168672ee"
                className="font-mono"
              />
            </div>
            <Button type="submit" disabled={loading}>
              {loading ? t('common.loading', 'Loading…') : t('admin.superadminSetup.promote')}
            </Button>
          </form>
          {success && (
            <div className="mt-4 p-3 rounded-md bg-muted text-sm space-y-1">
              <p className="font-medium text-green-600 dark:text-green-400">
                {t('admin.superadminSetup.done', 'Done')}
              </p>
              <p>
                {success.email} — role: {success.role}, is_active: {String(success.is_active)}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
};

export default SuperadminSetupPage;
